import React, { useEffect, useState } from "react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FieldArray, useFormik } from "formik";
import ProductItemServices from "../../../services/OrderingServices/ProductItemService";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
  CardHeader,
} from "reactstrap";
import * as Yup from "yup";
import Select from "react-select";
import CategoryServices from "../../../services/OrderingServices/CategoryService";
import UnitService from "../../../services/OrderingServices/UnitService";
import toast from "react-hot-toast";
import ItemService from "../../../services/OrderingServices/ItemService";
import FileDropzone from "../../../CosmosAdminPages/ResuableComponent/DropzoneComponent";

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  description: Yup.string(),

  minimunStockQuantity: Yup.number()
    .min(0, "Quantity cannot be negative")
    .when("isStockManaged", {
      is: true,
      then: (schema) => schema.required("Minimum stock is required"),
    }),

  itemPosition: Yup.number()
    .min(0, "Item position cannot be negative")
    .required("Item position is required"),
  price: Yup.number()
    .min(0, "Price cannot be negative")
    .required("Price is required"),
  tags: Yup.string(),
  images: Yup.string(),
  categoryId: Yup.number()
    .min(1, "Please select a category")
    .required("Category is required"),
  unitId: Yup.number()
    .min(1, "Please select a unit")
    .required("Unit is required"),
  // isMenuDisplay: Yup.boolean(),
  isHidden: Yup.boolean(),
  isProductSoldOut: Yup.boolean(),
  attributes: Yup.array().of(
    Yup.object({
      title: Yup.string(),
      value: Yup.number().min(0, "Attribute value cannot be negative"),
      isActive: Yup.boolean(),
    })
  ),
});

const ItemForm = () => {
  const breadcrumbItems = [{ title: "Back To List", link: "/menu-item" }];
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categoryData, setCategoryData] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [open, setOpen] = useState("0"); // Manage which accordion item is open
  const [selectedFiles, setSelectedFiles] = useState([]);
  const toggle = (id) => {
    setOpen(open === id ? "" : id); // Toggle the accordion item
  };
  const navigate = useNavigate();
  const fetchCategory = async () => {
    try {
      const response = await CategoryServices.getAllForDropdown();
      setCategoryData(response);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchUnit = async () => {
    try {
      setLoading(true);
      const response = await UnitService.getList();
      setUnitList(response);
    } catch (error) {
      console.log("🚀 ~ error:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await ItemService.itemList();
      setProductList(response);
    } catch (error) {
      console.log("🚀 ~ error:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchCategory();
    fetchUnit();
    fetchProduct();
  }, []);

  const [initialData, setInitialData] = useState([]);
  console.log("🚀 ~ ItemForm ~ initialData:", initialData);
  const fetchItemDetail = async () => {
    try {
      const response = await ItemService.getDetail(id);
      console.log("🚀 ~ fetchItemDetail ~ response:", response);

      // Ensure children have childProductId set so the select auto-selects
      // Existing API sometimes returns children without `childProductId` but their `id` is the product id
      const normalizedChildren = (response?.children || []).map((c) => ({
        ...c,
        childProductId: c.childProductId ?? c.id,
      }));

      setInitialData({ ...response, children: normalizedChildren });
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    fetchItemDetail();
  }, [id]);

  useEffect(() => {
    if (initialData?.displayImage?.getCDNUrl() && selectedFiles.length === 0) {
      setSelectedFiles([
        {
          isExisting: true,
          preview: initialData?.displayImage?.getCDNUrl(),
          // Some dropzones expect payload: null or undefined for existing
          // payload: null,
        },
      ]);
    } else if (!initialData?.displayImage?.getCDNUrl()) {
      setSelectedFiles([]); // clear if no image
    }
  }, [initialData?.displayImage?.getCDNUrl()]);

  useEffect(() => {
    setSelectedFiles([]);
  }, [id]);

  useEffect(() => {
    if (initialData?.displayImage?.getCDNUrl()) {
      setSelectedFiles([
        {
          isExisting: true,
          preview: initialData.displayImage.getCDNUrl(),
        },
      ]);
    } else {
      setSelectedFiles([]);
    }
  }, [initialData?.displayImage?.getCDNUrl()]);
  const formik = useFormik({
    initialValues: {
      id: initialData?.id,
      name: initialData?.name || "",
      slug: initialData?.slug || "",
      description: initialData?.description || "",
      price: initialData?.price || 0,
      tags: initialData?.tags || "",
      image: initialData?.image || "",
      categoryId: initialData?.categoryId || "",
      parentId: initialData?.parentId || null,
      unitId: initialData?.unitId || null,
      isHidden: initialData?.isHidden || false,
      isProductSoldOut: initialData?.isProductSoldOut || false,
      attributes: initialData?.attributes || [],
      children: initialData?.children || [],
      itemPosition: initialData?.productPosition || 0,
      minimunStockQuantity: 0,
      isStockManaged: false,
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsSubmitting(true);

        // Create FormData object for multipart/form-data
        const formData = new FormData();

        // Add basic form fields
        formData.append("id", values.id);

        formData.append("name", values.name);
        formData.append("slug", values.slug || "");
        formData.append("description", values.description);
        formData.append("price", values.price.toString());
        formData.append("tags", values.tags);
        formData.append("isHidden", values.isHidden.toString());
        formData.append("isProductSoldOut", values.isProductSoldOut.toString());
        formData.append("productPosition", values.itemPosition.toString());
        formData.append(
          "MinimunStockQuantity",
          values.minimunStockQuantity.toString()
        );
        formData.append("IsStockManaged", values.isStockManaged.toString());

        // Add optional fields only if they have values
        if (values.categoryId) {
          formData.append("categoryId", values.categoryId.toString());
        }

        if (values.unitId) {
          formData.append("unitId", values.unitId.toString());
        }

        if (values.parentId) {
          formData.append("parentId", values.parentId.toString());
        }

        // ✅ handle image - CORRECTED
        if (selectedFiles.length > 0) {
          const fileObj = selectedFiles[0];

          // Only append if it's a NEW file upload (not existing image)
          if (!fileObj.isExisting && fileObj.payload instanceof File) {
            formData.append("Image", fileObj.payload); // Use .payload, not the wrapper object
            console.log(
              "✅ New image attached:",
              fileObj.payload.name,
              fileObj.payload.type
            );
          } else if (fileObj.isExisting) {
            console.log("📝 Keeping existing image - not sending new file");
          }
        }

        // Handle attributes array - filter out empty titles
        const filteredAttributes = values.attributes.filter(
          (attr) => attr.title.trim() !== ""
        );

        if (filteredAttributes.length > 0) {
          // Option 1: Send as JSON string
          // formData.append('attributes', JSON.stringify(filteredAttributes));

          // Option 2: Send each attribute separately (if your backend expects this format)
          filteredAttributes.forEach((attr, index) => {
            formData.append(`attributes[${index}][id]`, attr.id.toString());
            formData.append(`attributes[${index}][title]`, attr.title);
            formData.append(
              `attributes[${index}][value]`,
              attr.value.toString()
            );
            formData.append(
              `attributes[${index}][isActive]`,
              attr.isActive.toString()
            );
          });
        }

        // Handle children array
        if (values.children && values.children.length > 0) {
          // Send each child separately with indexed format
          values.children.forEach((child, index) => {
            formData.append(`children[${index}][id]`, child.id.toString());
            if (child.childProductId) {
              formData.append(
                `children[${index}][childProductId]`,
                child.childProductId.toString()
              );
            }
            formData.append(`children[${index}][name]`, child.name || "");
            formData.append(
              `children[${index}][price]`,
              child.price?.toString() || "0"
            );
            formData.append(
              `children[${index}][description]`,
              child.description || ""
            );
            formData.append(
              `children[${index}][choices]`,
              child.choices?.toString() || "0"
            );
          });
        }

        // Debug: Log FormData contents
        console.log("FormData contents:");
        for (let pair of formData.entries()) {
          console.log("Hello", pair[0], pair[1]); // log pair[1] directly without string concatenation
          if (pair[1] instanceof File) {
            console.log("File name:", pair[1].name, "File type:", pair[1].type);
          }
        }

        if (id) {
          await ProductItemServices.update(id, formData);
          navigate("/menu-item", {
            state: { refetch: true },
          });

          toast.success("Product updated successfully!");
        } else {
          await ProductItemServices.create(formData);
          resetForm();
          navigate("/menu-item");
          toast.success("Product created successfully!");
        }
      } catch (error) {
        console.error("Submit Error:", error);
        toast.error("Failed to save product. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  console.log("🚀 ~ ItemForm ~ formik:", formik);
  const handleAddChildrenAttribute = () => {
    const newParentItem = {
      id: 0,
      childProductId: null,
      name: "",
      price: null,
      description: "",
      choices: null,
    };
    const updatedParentItems = [...formik.values.children, newParentItem];
    formik.setFieldValue("children", updatedParentItems);
  };

  const handleRemoveParent = (index) => {
    const updatedItems = [...formik.values.children];
    updatedItems.splice(index, 1);
    formik.setFieldValue("children", updatedItems);
  };
  const handleAddAttribute = () => {
    const newAttributeItem = {
      id: 0,
      title: "",
      value: 0,
      isActive: true,
    };
    const updatedAttributeItems = [
      ...formik.values.attributes,
      newAttributeItem,
    ];
    formik.setFieldValue("attributes", updatedAttributeItems);
  };

  const handleRemoveAttribute = (index) => {
    const updatedItems = [...formik.values.attributes];
    updatedItems.splice(index, 1);
    formik.setFieldValue("attributes", updatedItems);
  };

  const handleCategoryChange = (selectedOption) => {
    formik.setFieldValue(
      "categoryId",
      selectedOption ? selectedOption.value : null
    );
  };

  const handleUnitChange = (selectedOption) => {
    formik.setFieldValue(
      "unitId",
      selectedOption ? selectedOption.value : null
    );
  };
  const handleParentChange = (selectedOption) => {
    console.log("🚀 ~ handleParentChange ~ selectedOption:", selectedOption);
    formik.setFieldValue(
      "parentId",
      selectedOption ? selectedOption.value : null
    );
  };
  const categoryOptions =
    categoryData?.items?.map((type) => ({
      value: type.id,
      label: type.name,
    })) || [];

  const selectedCategory = categoryOptions.find(
    (option) => option.value === formik.values.categoryId
  );

  const unitOptions =
    unitList?.map((type) => ({
      value: type.id,
      label: type.name,
    })) || [];

  const selectedUnit = unitOptions.find(
    (option) => option.value === formik.values.unitId
  );
  // Build parent options from product list, and include any children from the current item
  let parentOptions = (productList?.items || []).map((p) => ({
    value: p.id,
    label: p.name,
    price: p.price,
    description: p.description,
    choices: p.choices,
  }));

  // Ensure children from the loaded item (initialData) are present as options so selects can auto-select
  const existingValues = new Set(parentOptions.map((o) => o.value));
  (initialData?.children || []).forEach((c) => {
    const childId = c.childProductId ?? c.id;
    if (childId && !existingValues.has(childId)) {
      parentOptions.push({
        value: childId,
        label: c.name || `Item ${childId}`,
        price: c.price ?? 0,
        description: c.description ?? "",
        choices: c.choices ?? 0,
      });
      existingValues.add(childId);
    }
  });
  useEffect(() => {
    if (!formik.values.isStockManaged) {
      formik.setFieldValue("minimunStockQuantity", 0);
    }
  }, [formik.values.isStockManaged]);

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb
          pageTitle="Products"
          title="Add Product"
          breadcrumbItems={breadcrumbItems}
        />

        <form onSubmit={formik.handleSubmit}>
          <Row style={{ display: "flex", alignItems: "stretch" }}>
            <Col lg={4} style={{ display: "flex", flexDirection: "column" }}>
              <Card
                style={{
                  flex: 1,
                  marginBottom: "24px",
                  border: "1px solid #e9ecef",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
              >
                <CardHeader
                  style={{
                    backgroundColor: "#fff",
                    borderBottom: "1px solid #e9ecef",
                    padding: "16px 20px",
                  }}
                >
                  <h6 style={{ margin: 0, fontWeight: 600, color: "#495057" }}>
                    <i
                      className="ri-information-line"
                      style={{ marginRight: "8px" }}
                    ></i>
                    Basic Information
                  </h6>
                </CardHeader>
                <CardBody style={{ padding: "20px" }}>
                  <Row>
                    <Col lg={12}>
                      <FormGroup>
                        <Label
                          for="name"
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#495057",
                          }}
                        >
                          Product Name{" "}
                          <span style={{ color: "#dc3545" }}>*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          placeholder="Enter product name"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.name}
                          invalid={
                            !!(formik.touched.name && formik.errors.name)
                          }
                          style={{ fontSize: "14px" }}
                        />
                        <FormFeedback>{formik.errors.name}</FormFeedback>
                      </FormGroup>
                    </Col>

                    <Col lg={6}>
                      <FormGroup>
                        <Label
                          for="itemPosition"
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#495057",
                          }}
                        >
                          Item Position{" "}
                          <span style={{ color: "#dc3545" }}>*</span>
                        </Label>
                        <Input
                          id="itemPosition"
                          name="itemPosition"
                          type="number"
                          placeholder="Enter position"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.itemPosition}
                          invalid={
                            !!(
                              formik.touched.itemPosition &&
                              formik.errors.itemPosition
                            )
                          }
                          style={{ fontSize: "14px" }}
                        />
                        <FormFeedback>
                          {formik.errors.itemPosition}
                        </FormFeedback>
                      </FormGroup>
                    </Col>

                    <Col lg={6}>
                      <FormGroup>
                        <Label
                          for="price"
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#495057",
                          }}
                        >
                          Price <span style={{ color: "#dc3545" }}>*</span>
                        </Label>
                        <Input
                          id="price"
                          name="price"
                          type="number"
                          placeholder="Enter price"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.price}
                          invalid={
                            !!(formik.touched.price && formik.errors.price)
                          }
                          style={{ fontSize: "14px" }}
                        />
                        <FormFeedback>{formik.errors.price}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col lg={6}>
                      <FormGroup>
                        <Label
                          for="tags"
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#495057",
                          }}
                        >
                          Tags
                        </Label>
                        <Input
                          id="tags"
                          name="tags"
                          type="text"
                          placeholder="Enter tags separated by commas"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.tags}
                          style={{ fontSize: "14px" }}
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={6}>
                      <FormGroup>
                        <Label
                          for="minimunStockQuantity"
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#495057",
                          }}
                        >
                          Minimum Stock Quantity
                        </Label>
                        <Input
                          id="minimunStockQuantity"
                          name="minimunStockQuantity"
                          type="number"
                          placeholder="Enter minimum stock"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.minimunStockQuantity}
                          disabled={!formik.values.isStockManaged}
                          invalid={
                            !!(
                              formik.touched.minimunStockQuantity &&
                              formik.errors.minimunStockQuantity
                            )
                          }
                          style={{ fontSize: "14px" }}
                        />
                        <FormFeedback>
                          {formik.errors.minimunStockQuantity}
                        </FormFeedback>
                      </FormGroup>
                    </Col>

                    <Col lg={6}>
                      <FormGroup>
                        <Label for="categoryId">
                          Category <span style={{ color: "#dc3545" }}>*</span>
                        </Label>
                        <Select
                          name="categoryId"
                          value={
                            categoryOptions.find(
                              (option) =>
                                option.value === formik.values.categoryId
                            ) || null
                          }
                          onChange={(selectedOption) => {
                            formik.setFieldValue(
                              "categoryId",
                              selectedOption ? selectedOption.value : null
                            );
                          }}
                          onBlur={() =>
                            formik.setFieldTouched("categoryId", true)
                          }
                          options={categoryOptions}
                          placeholder="Select Category"
                          isClearable
                          isLoading={loading}
                          classNamePrefix="react-select"
                        />
                        <FormFeedback>{formik.errors.categoryId}</FormFeedback>
                      </FormGroup>
                    </Col>

                    <Col lg={6}>
                      <FormGroup>
                        <Label
                          for="unitId"
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#495057",
                          }}
                        >
                          Unit <span style={{ color: "#dc3545" }}>*</span>
                        </Label>
                        <Select
                          name="unitId"
                          value={
                            unitOptions.find(
                              (option) => option.value === formik.values.unitId
                            ) || null
                          }
                          onChange={(selectedOption) => {
                            formik.setFieldValue(
                              "unitId",
                              selectedOption ? selectedOption.value : null
                            );
                          }}
                          onBlur={() => formik.setFieldTouched("unitId", true)}
                          options={unitOptions}
                          placeholder="Select Unit"
                          isClearable
                          isLoading={loading}
                          classNamePrefix="react-select"
                        />
                        <FormFeedback>{formik.errors.unitId}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col lg={12}>
                      <FormGroup>
                        <Label
                          for="description"
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#495057",
                          }}
                        >
                          Description
                        </Label>
                        <Input
                          id="description"
                          name="description"
                          type="textarea"
                          rows={4}
                          placeholder="Enter product description"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.description}
                          style={{ fontSize: "14px" }}
                        />
                      </FormGroup>
                    </Col>

                    <Col lg={12}>
                      <div
                        style={{
                          padding: "16px",
                          backgroundColor: "#f8f9fa",
                          borderRadius: "6px",
                          border: "1px solid #e9ecef",
                        }}
                      >
                        <Row>
                          <Col lg={4}>
                            <FormGroup check>
                              <Label
                                check
                                style={{
                                  fontSize: "14px",
                                  color: "#495057",
                                  cursor: "pointer",
                                }}
                              >
                                <Input
                                  type="checkbox"
                                  name="isHidden"
                                  onChange={formik.handleChange}
                                  checked={formik.values.isHidden}
                                  style={{ marginRight: "8px" }}
                                />
                                Hide from POS
                              </Label>
                            </FormGroup>
                          </Col>
                          <Col lg={4}>
                            <FormGroup check>
                              <Label
                                check
                                style={{
                                  fontSize: "14px",
                                  color: "#495057",
                                  cursor: "pointer",
                                }}
                              >
                                <Input
                                  type="checkbox"
                                  name="isProductSoldOut"
                                  onChange={formik.handleChange}
                                  checked={formik.values.isProductSoldOut}
                                  style={{ marginRight: "8px" }}
                                />
                                Sold Out
                              </Label>
                            </FormGroup>
                          </Col>
                          <Col lg={4}>
                            <FormGroup check>
                              <Label
                                check
                                style={{
                                  fontSize: "14px",
                                  color: "#495057",
                                  cursor: "pointer",
                                }}
                              >
                                <Input
                                  type="checkbox"
                                  name="isStockManaged"
                                  onChange={formik.handleChange}
                                  checked={formik.values.isStockManaged}
                                  style={{ marginRight: "8px" }}
                                />
                                Manage Stock
                              </Label>
                            </FormGroup>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            <Col lg={8} style={{ display: "flex", flexDirection: "column" }}>
              <Card
                style={{
                  flex: 1,
                  marginBottom: "24px",
                  border: "1px solid #e9ecef",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  maxHeight: "800px", // height limit
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardHeader
                  style={{
                    backgroundColor: "#fff",
                    borderBottom: "1px solid #e9ecef",
                    padding: "16px 20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h6 style={{ margin: 0, fontWeight: 600, color: "#495057" }}>
                    <i
                      className="ri-parent-line"
                      style={{ marginRight: "8px" }}
                    ></i>
                    Children Items
                  </h6>
                  <Button
                    color="success"
                    size="sm"
                    type="button"
                    onClick={handleAddChildrenAttribute}
                    style={{ fontSize: "13px" }}
                  >
                    <i className="ri-add-line"></i> Add Children
                  </Button>
                </CardHeader>

                <CardBody
                  style={{
                    padding: "20px",
                    overflowY: "auto", // scroll if exceeds height
                  }}
                >
                  {formik.values.children &&
                  formik.values.children.length > 0 ? (
                    <div style={{ overflowX: "auto" }}>
                      <Table
                        bordered
                        style={{ marginBottom: 0, fontSize: "14px" }}
                      >
                        <thead style={{ backgroundColor: "#f8f9fa" }}>
                          <tr>
                            <th
                              style={{
                                width: "50px",
                                fontWeight: 600,
                                color: "#495057",
                              }}
                            >
                              S.N
                            </th>
                            <th style={{ fontWeight: 600, color: "#495057" }}>
                              Product
                            </th>
                            <th
                              style={{
                                width: "100px",
                                fontWeight: 600,
                                color: "#495057",
                              }}
                            >
                              Price
                            </th>
                            <th
                              style={{
                                width: "100px",
                                fontWeight: 600,
                                color: "#495057",
                              }}
                            >
                              Choices
                            </th>
                            <th style={{ fontWeight: 600, color: "#495057" }}>
                              Description
                            </th>
                            <th
                              style={{
                                width: "80px",
                                fontWeight: 600,
                                color: "#495057",
                                textAlign: "center",
                              }}
                            >
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {formik.values.children.map((item, index) => (
                            <tr key={index}>
                              <td
                                style={{
                                  verticalAlign: "middle",
                                  color: "#6c757d",
                                }}
                              >
                                {index + 1}
                              </td>
                              <td>
                                <Input
                                  type="select"
                                  name={`children[${index}].childProductId`}
                                  onChange={(e) => {
                                    const selected = parentOptions.find(
                                      (p) =>
                                        p.value === parseInt(e.target.value)
                                    );
                                    if (selected) {
                                      formik.setFieldValue(
                                        `children[${index}].childProductId`,
                                        selected.value
                                      );
                                      formik.setFieldValue(
                                        `children[${index}].name`,
                                        selected.label
                                      );
                                      formik.setFieldValue(
                                        `children[${index}].price`,
                                        selected.price
                                      );
                                      formik.setFieldValue(
                                        `children[${index}].description`,
                                        selected.description
                                      );
                                      formik.setFieldValue(
                                        `children[${index}].choices`,
                                        selected.choices
                                      );
                                    }
                                  }}
                                  value={item.childProductId || ""}
                                  style={{ fontSize: "14px" }}
                                >
                                  <option value="">Select Product</option>
                                  {parentOptions.map((p) => (
                                    <option key={p.value} value={p.value}>
                                      {p.label}
                                    </option>
                                  ))}
                                </Input>
                              </td>
                              <td>
                                <Input
                                  name={`children[${index}].price`}
                                  type="number"
                                  value={item.price || ""}
                                  onChange={formik.handleChange}
                                  placeholder="Price"
                                  style={{ fontSize: "14px" }}
                                />
                              </td>
                              <td>
                                <Input
                                  name={`children[${index}].choices`}
                                  type="number"
                                  value={item.choices || ""}
                                  onChange={formik.handleChange}
                                  placeholder="Choices"
                                  style={{ fontSize: "14px" }}
                                />
                              </td>
                              <td>
                                <Input
                                  name={`children[${index}].description`}
                                  type="textarea"
                                  rows={2}
                                  value={item.description || ""}
                                  onChange={formik.handleChange}
                                  placeholder="Description"
                                  style={{ fontSize: "14px" }}
                                />
                              </td>
                              <td
                                style={{
                                  textAlign: "center",
                                  verticalAlign: "middle",
                                }}
                              >
                                <Button
                                  color="light"
                                  size="sm"
                                  type="button"
                                  onClick={() => handleRemoveParent(index)}
                                  style={{ fontSize: "12px" }}
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px 20px",
                        color: "#6c757d",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px",
                        border: "2px dashed #dee2e6",
                      }}
                    >
                      <i
                        className="ri-inbox-line"
                        style={{
                          fontSize: "48px",
                          color: "#adb5bd",
                          marginBottom: "12px",
                        }}
                      ></i>
                      <p style={{ margin: 0, fontSize: "14px" }}>
                        No children items added yet
                      </p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row style={{ display: "flex", alignItems: "stretch" }}>
            <Col lg={4} style={{ display: "flex", flexDirection: "column" }}>
              {" "}
              <Card
                style={{
                  flex: 1,
                  marginBottom: "24px",
                  border: "1px solid #e9ecef",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
              >
                <CardHeader
                  style={{
                    backgroundColor: "#fff",
                    borderBottom: "1px solid #e9ecef",
                    padding: "16px 20px",
                  }}
                >
                  <h6 style={{ margin: 0, fontWeight: 600, color: "#495057" }}>
                    <i
                      className="ri-image-line"
                      style={{ marginRight: "8px" }}
                    ></i>
                    Product Image
                  </h6>
                </CardHeader>
                <CardBody style={{ padding: "20px" }}>
                  <FileDropzone
                    selectedFiles={selectedFiles}
                    setSelectedFiles={setSelectedFiles}
                    formik={formik}
                    existingImage={initialData?.displayImage?.getCDNUrl()}
                    customStyles={{
                      card: {
                        marginBottom: "24px",
                        border: "1px solid #e9ecef",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      },
                      header: {
                        backgroundColor: "#fff",
                        borderBottom: "1px solid #e9ecef",
                        padding: "16px 20px",
                        fontWeight: 600,
                        color: "#495057",
                      },
                      body: {
                        padding: "20px",
                      },
                      dropzone: {
                        border: "2px dashed #dee2e6",
                        borderRadius: "8px",
                        padding: "40px 20px",
                        textAlign: "center",
                        backgroundColor: "#f8f9fa",
                        cursor: "pointer",
                      },
                      icon: {
                        fontSize: "48px",
                        color: "#adb5bd",
                        marginBottom: "12px",
                      },
                      title: {
                        margin: "0 0 8px 0",
                        color: "#495057",
                        fontSize: "14px",
                        fontWeight: 500,
                      },
                      subtitle: {
                        margin: 0,
                        color: "#6c757d",
                        fontSize: "12px",
                      },
                    }}
                  />
                </CardBody>
              </Card>
            </Col>

            <Col lg={8} style={{ display: "flex", flexDirection: "column" }}>
              <Card
                style={{
                  flex: 1,
                  marginBottom: "24px",
                  border: "1px solid #e9ecef",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
              >
                <CardHeader
                  style={{
                    backgroundColor: "#fff",
                    borderBottom: "1px solid #e9ecef",
                    padding: "16px 20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <h6
                      style={{ margin: 0, fontWeight: 600, color: "#495057" }}
                    >
                      <i
                        className="ri-list-settings-line"
                        style={{ marginRight: "8px" }}
                      ></i>
                      Attributes
                    </h6>
                    <Button
                      color="success"
                      size="sm"
                      type="button"
                      onClick={handleAddAttribute}
                      style={{ fontSize: "13px" }}
                    >
                      <i className="ri-add-line"></i> Add Attribute
                    </Button>
                  </div>
                </CardHeader>
                <CardBody style={{ padding: "20px" }}>
                  {formik.values.attributes &&
                  formik.values.attributes.length > 0 ? (
                    <div style={{ overflowX: "auto" }}>
                      <Table
                        bordered
                        style={{ marginBottom: 0, fontSize: "14px" }}
                      >
                        <thead style={{ backgroundColor: "#f8f9fa" }}>
                          <tr>
                            <th
                              style={{
                                width: "60px",
                                fontWeight: 600,
                                color: "#495057",
                              }}
                            >
                              S.N
                            </th>
                            <th style={{ fontWeight: 600, color: "#495057" }}>
                              Title
                            </th>
                            <th
                              style={{
                                width: "120px",
                                fontWeight: 600,
                                color: "#495057",
                              }}
                            >
                              Price
                            </th>
                            <th
                              style={{
                                width: "100px",
                                fontWeight: 600,
                                color: "#495057",
                                textAlign: "center",
                              }}
                            >
                              Active
                            </th>
                            <th
                              style={{
                                width: "80px",
                                fontWeight: 600,
                                color: "#495057",
                                textAlign: "center",
                              }}
                            >
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {formik.values.attributes.map((item, index) => (
                            <tr key={index}>
                              <td
                                style={{
                                  verticalAlign: "middle",
                                  color: "#6c757d",
                                }}
                              >
                                {index + 1}
                              </td>
                              <td>
                                <Input
                                  name={`attributes[${index}].title`}
                                  value={item.title}
                                  onChange={formik.handleChange}
                                  placeholder="Attribute Title"
                                  style={{ fontSize: "14px" }}
                                  invalid={
                                    !!(
                                      formik.touched.attributes?.[index]
                                        ?.title &&
                                      formik.errors.attributes?.[index]?.title
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <Input
                                  name={`attributes[${index}].value`}
                                  type="number"
                                  value={item.value}
                                  onChange={formik.handleChange}
                                  placeholder="Price"
                                  style={{ fontSize: "14px" }}
                                  invalid={
                                    !!(
                                      formik.touched.attributes?.[index]
                                        ?.value &&
                                      formik.errors.attributes?.[index]?.value
                                    )
                                  }
                                />
                              </td>
                              <td
                                style={{
                                  textAlign: "center",
                                  verticalAlign: "middle",
                                }}
                              >
                                <Input
                                  type="checkbox"
                                  name={`attributes[${index}].isActive`}
                                  checked={item.isActive}
                                  onChange={formik.handleChange}
                                  style={{ cursor: "pointer" }}
                                />
                              </td>
                              <td
                                style={{
                                  textAlign: "center",
                                  verticalAlign: "middle",
                                }}
                              >
                                <Button
                                  color="light"
                                  size="sm"
                                  type="button"
                                  onClick={() => handleRemoveAttribute(index)}
                                  style={{ fontSize: "12px" }}
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px 20px",
                        color: "#6c757d",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px",
                        border: "2px dashed #dee2e6",
                      }}
                    >
                      <i
                        className="ri-inbox-line"
                        style={{
                          fontSize: "48px",
                          color: "#adb5bd",
                          marginBottom: "12px",
                        }}
                      ></i>
                      <p style={{ margin: 0, fontSize: "14px" }}>
                        No attributes added yet
                      </p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              paddingTop: "12px",
              borderTop: "1px solid #e9ecef",
            }}
          >
            <Link to="/menu-item">
              <Button
                color="danger"
                style={{ fontSize: "14px", fontWeight: 500 }}
              >
                <i className="ri-close-line" style={{ marginRight: "6px" }}></i>
                Cancel
              </Button>
            </Link>
            <Button
              type="submit"
              color="success"
              style={{ fontSize: "14px", fontWeight: 500 }}
            >
              <i className="ri-save-line" style={{ marginRight: "6px" }}></i>
              Save Product
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ItemForm;
